import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import BackNavbar from '../../../ReusableBlocks/Navbar';
import Footer from '../../../ReusableBlocks/FooterBlock';
import { Link, useLocation } from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import { useSnackbar } from 'notistack';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContent } from '../../../Redux/Dashboard/External/ContentStore/ContentStoreAction';
import { contentSearch } from '../../../Redux/Dashboard/External/Search/ContentSearchAction';
import LoadingScreen from '../../../ReusableBlocks/LoadingScreen';
import ReactPlayer from 'react-player';
import { getContentStoreCardBackgroundStyle, getMediaProvider, normalizeMediaUrl } from '../../../Utils/mediaUrls';


// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="spinner">
    <div className="double-bounce1"></div>
    <div className="double-bounce2"></div>
  </div>
);

const isVideoContent = (file) => ['Video', 'Demo Shorts', 'Shorts'].includes(file?.contentType);

const getReleaseNoteKey = (file) => `${file.fileName}-${file.releaseDate}`;


const Content_Store_Files = (props) => {



  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const { fileformat } = useParams();
  const dispatch = useDispatch();

  const [showFileModal, setFileModal] = useState(false);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState('');
  const [playing, setPlaying] = useState(false);

  const handlePlayButtonClick = () => {
    setPlaying(true);
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
  };

  const { content, loading: isLoading, error: errorMessage } = useSelector(state => state.Content);
  const { results } = useSelector(state => state.ContentSearch);
  const isLoadingSer = useSelector(state => state.ContentSearch?.loading);
  const errorMessageser = useSelector(state => state.ContentSearch?.error);

  useEffect(() => {
    dispatch(fetchContent());
  }, [dispatch]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      // When search query is empty, show default content
      if (content) {
        const filesByFormat = content.filter(file => file.contentType === fileformat);
        const sortedFiles = filesByFormat.sort((a, b) => a.fileName.localeCompare(b.fileName));
        setFilteredFiles(sortedFiles);
        setSearchResults(sortedFiles);
        setHasSearched(false);
        setNoResults(false);
      }
    }
  }, [searchQuery, fileformat, content]);

  useEffect(() => {
    if (hasSearched) {
      // When search query is not empty, show search results
      if (results?.result?.length > 0) {
        setSearchResults(results?.result.filter(file => file.contentType === fileformat));
        setNoResults(false);
      } else if (!isLoadingSer && !errorMessageser) {
        setSearchResults([]);
        setNoResults(true);
      } else if (errorMessageser) {
        enqueueSnackbar('Failed to fetch search results.', { variant: 'error' });
      }
    }
  }, [hasSearched, results, isLoadingSer, errorMessageser, fileformat]);

  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
  }, [location]);

  useEffect(() => {
    if (fileformat && content) {
      const filesByFormat = content.filter(file => file.contentType === fileformat);
      const sortedFiles = filesByFormat.sort((a, b) => a.fileName.localeCompare(b.fileName));
      setFilteredFiles(sortedFiles);
      setSearchResults(sortedFiles);
    }
  }, [fileformat, content]);

  useEffect(() => {
    if (errorMessage && errorMessage !== 'You have not completed mandatory Tech Central Training, please complete it.') {
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Retry',
        customClass: {
          title: 'my-custom-title-of-pop-login',
          confirmButton: 'my-custom-button-of-pop',
        },
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
    }
  }, [errorMessage]);

  const handleShowFileModal = (file) => {
    let modifiedViewUrl = normalizeMediaUrl(file.viewUrl, 'preview');
    if (getMediaProvider(file.viewUrl) !== 'google-drive' && isVideoContent(file)) {
      modifiedViewUrl = modifiedViewUrl.replace('/view', '/preview');
    } else if (getMediaProvider(file.viewUrl) !== 'google-drive' && file.contentType === 'Presentation') {
      modifiedViewUrl += '&rm=minimal';
    }
    setSelectedFile({ ...file, viewUrl: modifiedViewUrl });
    setFileModal(true);
  };

  const handleCloseFileModal = () => setFileModal(false);

  const getVideoDownloadFileName = (file) => {
    const fileName = file.fileName || file.title || file.name || 'video';

    return /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}.mp4`;
  };

  const downloadVideo = async (videoUrl, file) => {
    setDownloading(true);
    setWaitingMessage('Please wait, the download is starting and may take some time.');

    try {
      const response = await fetch(videoUrl, { mode: 'cors' });

      if (!response.ok) {
        throw new Error('Network response was not ok.');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getVideoDownloadFileName(file);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download video as blob. Falling back to opening the URL.', error);
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
      setWaitingMessage('');
    }
  };

  const downloadFile = (file) => {
    let downloadUrl;

    if (isVideoContent(file)) {
      const videoDownloadUrl = file.downloadUrl || file.viewUrl;
      const normalizedVideoDownloadUrl = normalizeMediaUrl(videoDownloadUrl, 'download');

      if (getMediaProvider(videoDownloadUrl) === 'google-drive') {
        window.open(normalizedVideoDownloadUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      downloadVideo(normalizedVideoDownloadUrl, file);
      return;
    }

    setDownloading(true);
    setWaitingMessage('Please wait, the download is starting and may take some time.');

    downloadUrl = normalizeMediaUrl(file.downloadUrl, 'download');

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${file.fileName}.${file.fileExtension}`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);

    setDownloading(false);
    setWaitingMessage('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        enqueueSnackbar('Password copied to clipboard!', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to copy Password.', { variant: 'error' });
      });
  };



  const getHeading = () => {
    switch (fileformat) {
      case 'Presentation':
        return 'Presentation';
      case 'Video':
        return 'Video';
      case 'Playbook':
        return 'Playbook';
      case 'Release Note':
        return 'Release Notes';
      case 'Release Notes':
        return 'Release Notes';
      case 'Shorts':
        return 'Shorts';
      default:
        return 'Files';
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      enqueueSnackbar('Please enter a search term.', { variant: 'error' });

      return;
    }
    setHasSearched(true); // Set hasSearched to true when the search is submitted
    dispatch(contentSearch(searchQuery));
  };


  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit(e); // Pass the event object here
    }
  };

  return (
    <>
      {isLoading && <LoadingScreen text="Loading.." />}
      <BackNavbar />

      <div className='content-store-main-background'>
        <div className='content-store-top-header-section-format'>
          <div className="header-content">
            <h1 className='content-store-head-h1'>Discover the {getHeading()} here!</h1>
          </div>
          <div className="search-form-content">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onKeyDown={handleKeyDown}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" onClick={handleSearchSubmit}>
              <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
        <br />
        <div className='result-section'>
          <div className='container'>
            <div className='result-sec-flex'>
              <div>
                <h6>{getHeading()}</h6>
                <div className="line"></div>
              </div>
            </div>
            <div>
              <div className="row">

                {isLoadingSer && <LoadingSpinner />}
                {errorMessageser && <p>Error: {errorMessageser}</p>}

                {!isLoadingSer && searchResults.map(file => (
                  <div className="col-sm-3" key={file.contentType === 'Release Note' ? getReleaseNoteKey(file) : file.fileName}>
                    {file.contentType === 'Presentation' && (
                      <div className="bg-primary-ppt" style={{ backgroundImage: `linear-gradient(to bottom, rgba(5, 33, 64, 0), rgba(0, 0, 0, 0.5)), url(${normalizeMediaUrl(file.thumbnail, 'background')})` }} onClick={() => handleShowFileModal(file)}>{file.fileName}</div>
                    )}
                    {file.contentType === 'Video' && (
                      <div className="bg-primary-video" style={{ backgroundImage: `linear-gradient(to bottom, rgba(5, 33, 64, 0), rgba(0, 0, 0, 0.5)), url(${normalizeMediaUrl(file.thumbnail, 'background')})` }} onClick={() => handleShowFileModal(file)}>{file.fileName}</div>
                    )}
                     {file.contentType === 'Shorts' && (
                      <div className="bg-primary-video" style={{ backgroundImage: `linear-gradient(to bottom, rgba(5, 33, 64, 0), rgba(0, 0, 0, 0.5)), url(${normalizeMediaUrl(file.thumbnail, 'background')})` }} onClick={() => handleShowFileModal(file)}>{file.fileName}</div>
                    )}
                    {file.contentType === 'Playbook' && (
                      <div className="bg-primary-playbook" style={{ backgroundImage: `linear-gradient(to bottom, rgba(5, 33, 64, 0), rgba(0, 0, 0, 0.5)), url(${normalizeMediaUrl(file.thumbnail, 'background')})` }} onClick={() => handleShowFileModal(file)}>{file.fileName}</div>
                    )}
                    {file.contentType === 'Release Note' && (
                      <div
                        className="bg-primary-release"
                        style={getContentStoreCardBackgroundStyle(file.thumbnail)}
                        onClick={() => handleShowFileModal(file)}
                      >
                        <div className="file-info">
                          <p className='relase-notes-title'>{file.fileName}</p>
                        </div>
                      </div>
                    )}
                    <br></br>
                  </div>
                ))}
                {!isLoadingSer && !searchResults.length && noResults && (
                  <div className="no-results">
                    <p>No files match the search criteria.</p>
                  </div>
                )}



              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        show={showFileModal} fullscreen={true} >
        <Modal.Body className='content-store-popup'>
          <div className="container">
            <div className='modal-div-learning'>
              <div className="row">
                <div className="col-sm-8 mb-3 mb-sm-0">
                  {selectedFile && (
                    <>
                      {isVideoContent(selectedFile) && getMediaProvider(selectedFile.viewUrl) !== 'google-drive' ? (
                        <div className="video-wrapper" onContextMenu={handleContextMenu}>
                          <div className="custom-react-player">
                            <ReactPlayer
                              url={selectedFile.viewUrl}
                              controls
                              playing={playing}
                              width="100%"
                              height="100%"
                              light={normalizeMediaUrl(selectedFile?.thumbnail, 'thumbnail')}
                              playIcon={
                                <button className="custom-play-button" onClick={handlePlayButtonClick}>
                                  ▶️
                                </button>
                              }
                            /></div>
                        </div>
                      ) : (
                        <div className="file-container">
                          <iframe
                            src={normalizeMediaUrl(selectedFile.viewUrl, 'preview')}
                            frameborder="0"
                            width="100%"
                            height="100%"
                            allowfullscreen="true"
                            title="File Preview"
                          ></iframe>
                        </div>
                      )}
                      {isVideoContent(selectedFile) && (
                        <p className="video-download-hint">Download is available from the video player controls.</p>
                      )}
                    </>
                  )}
                </div>
                <div className="col-sm-4 mb-3 mb-sm-0">
                  <div className="file-controls-dive">
                    <h1 className='file-tite-des'>{selectedFile && selectedFile.fileName}</h1>
                    <div className="line"></div>
                    {selectedFile && selectedFile.contentType === 'Playbook' && (
                      <p className='file-p-des'>Password :
                        <span
                          onClick={() => copyToClipboard('Playbook@123')}
                          style={{ cursor: 'pointer', }}
                          className='passowrd'
                        >
                          Copy Password
                        </span>
                      </p>
                    )}
                    {selectedFile && !isVideoContent(selectedFile) && (
                      <>
                        <button
                          className="card-cat-download"
                          onClick={() => downloadFile(selectedFile)}
                          disabled={downloading}
                        >
                          {downloading ? 'Downloading...' : 'Download'}
                        </button>
                        {waitingMessage && <p className='download-msg'>{waitingMessage}</p>}
                        <br />
                        <br />
                      </>
                    )}
                    <button className='card-cat-cancel' onClick={handleCloseFileModal}>Back</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>



    </>
  );
}

export default Content_Store_Files;
